import React, { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Line,
  LineChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { t } from '../../../utils/i18n';
import { useAuth } from '../../../context/AuthContext';
import { useApiEnvironment } from '../../../context/ApiEnvironmentContext';
import { getDashboardActivityTimeseries } from '../../../api/authAdmin';
import {
  CHART_AXIS_STROKE,
  CHART_GRID_STROKE,
  CHART_HEIGHT,
  CHART_PRIMARY,
  darkChartTooltipProps,
  formatChartDate,
} from '../../../utils/gaDashboard';
import DashboardPanel from '../components/DashboardPanel';
import DashboardKpi from '../components/DashboardKpi';
import ChartSkeleton from '../components/ChartSkeleton';

const ACTIVITY_DAYS = 30;

const MODERATION_SERIES = [
  { key: 'reports', color: 'rgb(var(--admin-chart-orange))', labelKey: 'dashboard.ops.series.reports' },
  { key: 'bans', color: 'rgb(var(--admin-chart-red))', labelKey: 'dashboard.ops.series.bans' },
  { key: 'unbans', color: 'rgb(var(--admin-chart-green))', labelKey: 'dashboard.ops.series.unbans' },
  { key: 'signups', color: CHART_PRIMARY, labelKey: 'dashboard.ops.series.signups' },
];

/** Social graph / content growth */
const GROWTH_SOCIAL_SERIES = [
  { key: 'likes', color: 'rgb(var(--admin-chart-orange))', labelKey: 'dashboard.ops.series.likes' },
  { key: 'fan_follows', color: 'rgb(var(--admin-chart-green))', labelKey: 'dashboard.ops.series.fanFollows' },
  { key: 'favorites', color: 'rgb(var(--admin-chart-purple))', labelKey: 'dashboard.ops.series.favorites' },
  { key: 'bubbles', color: 'rgb(var(--admin-chart-blue))', labelKey: 'dashboard.ops.series.bubbles' },
];

/** Presence / engagement (user-api: emoji, signal, link click) */
const GROWTH_ENGAGE_SERIES = [
  { key: 'emojis', color: 'rgb(var(--admin-chart-yellow))', labelKey: 'dashboard.ops.series.emojis' },
  { key: 'signals', color: 'rgb(var(--admin-chart-cyan))', labelKey: 'dashboard.ops.series.signals' },
  { key: 'link_clicks', color: 'rgb(var(--admin-chart-gray))', labelKey: 'dashboard.ops.series.linkClicks' },
  { key: 'signups', color: CHART_PRIMARY, labelKey: 'dashboard.ops.series.signups' },
];

const INACTIVE_REASON_KEYS = {
  1: 'dashboard.ops.inactiveReason.deactivation',
  2: 'dashboard.ops.inactiveReason.resignation',
  3: 'dashboard.ops.inactiveReason.ban',
  4: 'dashboard.ops.inactiveReason.suspension',
};

const REPORT_TYPE_KEYS = {
  1: 'spam',
  2: 'nudityOrPronography',
  3: 'sexualExploitationOrSolicitation',
  4: 'sharingPrivateImages',
  5: 'involvesChild',
  6: 'hateSpeechOrSymbols',
  7: 'bullyingOrHarassment',
  8: 'falseInformationHealth',
  9: 'falseInformationPolitics',
  10: 'falseInformationSocialIssue',
  11: 'digitallyCreatedOrAltered',
  12: 'somethingElse',
  13: 'violentThreat',
  14: 'animalAbuse',
  15: 'deathOrSevereInjury',
  16: 'dangerousOrganizationsOrIndividuals',
  17: 'scamOrFraud',
  18: 'intellectualPropertyViolation',
};

function activityDateRange() {
  const end = new Date();
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - (ACTIVITY_DAYS - 1));
  return {
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function DeepLink({ to, children }) {
  return (
    <Link to={to} className="text-xs font-medium text-primary hover:underline">
      {children}
    </Link>
  );
}

function RankedBars({ rows, barClassName = 'bg-primary/80' }) {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0) || 1;
  if (rows.length === 0) {
    return (
      <p className="py-6 text-center text-sm text-dark-text-secondary">{t('dashboard.noData')}</p>
    );
  }
  return (
    <div className="space-y-2.5">
      {rows.map((row) => (
        <div key={row.key}>
          <div className="mb-1 flex justify-between gap-2 text-xs">
            <span className="truncate text-dark-text-secondary" title={row.label}>
              {row.label}
            </span>
            <span className="shrink-0 font-medium text-dark-text">{row.count}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-dark-bg">
            <div
              className={`h-full rounded-full transition-all ${row.barClassName || barClassName}`}
              style={{ width: `${Math.round((row.count / max) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function StatusBreakdown({ reportsByStatus }) {
  const map = reportsByStatus || {};
  const rows = [
    {
      key: '1',
      label: t('report.status.requested'),
      count: Number(map['1'] ?? 0),
      barClassName: 'bg-primary',
    },
    {
      key: '2',
      label: t('report.status.completed'),
      count: Number(map['2'] ?? 0),
      barClassName: 'bg-emerald-500/80',
    },
    {
      key: '0',
      label: t('report.status.cancel'),
      count: Number(map['0'] ?? 0),
      barClassName: 'bg-dark-text-muted',
    },
  ];
  return <RankedBars rows={rows} />;
}

function ReportTypeBars({ reportsByType }) {
  const rows = useMemo(() => {
    const map = reportsByType || {};
    return Object.entries(map)
      .map(([type, count]) => {
        const n = Number(type);
        const key = REPORT_TYPE_KEYS[n];
        return {
          key: type,
          label: key ? t(`report.type.${key}`) : t('report.unknown', { value: type }),
          count: Number(count) || 0,
        };
      })
      .filter((r) => r.count > 0)
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);
  }, [reportsByType]);

  return <RankedBars rows={rows} barClassName="bg-orange-400/80" />;
}

function InactiveReasonBars({ inactiveByReason }) {
  const rows = useMemo(() => {
    const map = inactiveByReason || {};
    return [1, 2, 3, 4].map((code) => ({
      key: String(code),
      label: t(INACTIVE_REASON_KEYS[code]),
      count: Number(map[String(code)] ?? map[code] ?? 0),
    }));
  }, [inactiveByReason]);

  return <RankedBars rows={rows} barClassName="bg-red-400/70" />;
}

function MetricLinePanel({
  title,
  data,
  series,
  isLoading,
  className = '',
  heightClass = CHART_HEIGHT.panel,
}) {
  return (
    <DashboardPanel title={title} className={className}>
      {isLoading ? (
        <ChartSkeleton heightClass={heightClass} />
      ) : !data?.length ? (
        <p className={`${heightClass} flex items-center justify-center text-dark-text-secondary`}>
          {t('dashboard.noData')}
        </p>
      ) : (
        <div className={`${heightClass} animate-fade-in`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} />
              <XAxis
                dataKey="date"
                stroke={CHART_AXIS_STROKE}
                fontSize={11}
                tickFormatter={formatChartDate}
              />
              <YAxis stroke={CHART_AXIS_STROKE} fontSize={11} allowDecimals={false} width={32} />
              <Tooltip {...darkChartTooltipProps} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {series.map((s) => (
                <Line
                  key={s.key}
                  type="monotone"
                  dataKey={s.key}
                  name={t(s.labelKey)}
                  stroke={s.color}
                  strokeWidth={1.75}
                  dot={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </DashboardPanel>
  );
}

/**
 * Ops Pulse (moderation-heavy) + activity charts.
 */
export default function OpsPulseSection({ overview, onError }) {
  const { token } = useAuth();
  const { apiEnvironment } = useApiEnvironment();
  const { startDate, endDate } = activityDateRange();

  const activityQuery = useQuery({
    queryKey: ['dashboardActivityTimeseries', apiEnvironment, startDate, endDate],
    queryFn: () => getDashboardActivityTimeseries(startDate, endDate, token),
    enabled: !!token,
  });

  useEffect(() => {
    if (activityQuery.error && onError) onError(activityQuery.error);
  }, [activityQuery.error, onError]);

  const chartData = useMemo(() => {
    const series = activityQuery.data?.series ?? [];
    return series.map((row) => ({
      date: row.date,
      signups: row.signups ?? 0,
      reports: row.reports ?? 0,
      bans: row.bans ?? 0,
      unbans: row.unbans ?? 0,
      fan_follows: row.fan_follows ?? 0,
      bubbles: row.bubbles ?? 0,
      likes: row.likes ?? 0,
      signals: row.signals ?? 0,
      emojis: row.emojis ?? 0,
      link_clicks: row.link_clicks ?? 0,
      favorites: row.favorites ?? 0,
    }));
  }, [activityQuery.data]);

  const n = (v) => (v == null ? '—' : v);

  return (
    <section className="space-y-4 border-t border-dark-border pt-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-dark-text">{t('dashboard.ops.title')}</h2>
          <p className="mt-0.5 text-sm text-dark-text-secondary">{t('dashboard.ops.subtitle')}</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <DeepLink to="/report">{t('dashboard.ops.links.reports')}</DeepLink>
          <DeepLink to="/ban-history">{t('dashboard.ops.links.banHistory')}</DeepLink>
          <DeepLink to="/user">{t('dashboard.ops.links.users')}</DeepLink>
        </div>
      </div>

      {/* Moderation KPI strip — full width */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-8">
        <DashboardKpi
          label={t('dashboard.ops.kpi.reportsOpen')}
          value={n(overview?.reports_open)}
          accent
          size="primary"
        />
        <DashboardKpi
          label={t('dashboard.ops.kpi.reportsToday')}
          value={n(overview?.reports_today)}
          accent
          size="primary"
        />
        <DashboardKpi
          label={t('dashboard.ops.kpi.reportsThisWeek')}
          value={n(overview?.reports_this_week)}
          size="primary"
        />
        <DashboardKpi
          label={t('dashboard.ops.kpi.reportsCompleted')}
          value={n(overview?.reports_completed)}
          size="primary"
        />
        <DashboardKpi
          label={t('dashboard.ops.kpi.currentlyBanned')}
          value={n(overview?.currently_banned)}
          accent
          size="primary"
        />
        <DashboardKpi
          label={t('dashboard.ops.kpi.bansToday')}
          value={n(overview?.bans_today)}
          size="primary"
        />
        <DashboardKpi
          label={t('dashboard.ops.kpi.unbansToday')}
          value={n(overview?.unbans_today)}
          size="primary"
        />
        <DashboardKpi
          label={t('dashboard.ops.kpi.bansThisWeek')}
          value={n(overview?.bans_this_week)}
          size="primary"
        />
      </div>

      {/* Moderation + growth charts */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-12">
        <MetricLinePanel
          title={t('dashboard.ops.moderationChart')}
          data={chartData}
          series={MODERATION_SERIES}
          isLoading={activityQuery.isLoading}
          heightClass={CHART_HEIGHT.panel}
          className="xl:col-span-4"
        />
        <MetricLinePanel
          title={t('dashboard.ops.growthSocialChart')}
          data={chartData}
          series={GROWTH_SOCIAL_SERIES}
          isLoading={activityQuery.isLoading}
          heightClass={CHART_HEIGHT.panel}
          className="xl:col-span-4"
        />
        <MetricLinePanel
          title={t('dashboard.ops.growthEngageChart')}
          data={chartData}
          series={GROWTH_ENGAGE_SERIES}
          isLoading={activityQuery.isLoading}
          heightClass={CHART_HEIGHT.panel}
          className="xl:col-span-4"
        />
        <DashboardPanel
          title={t('dashboard.ops.reportStatusBreakdown')}
          className="xl:col-span-4"
        >
          <StatusBreakdown reportsByStatus={overview?.reports_by_status} />
          {(overview?.reports_canceled != null || overview?.unbans_this_week != null) && (
            <div className="mt-3 grid grid-cols-2 gap-2 border-t border-dark-border pt-2 text-xs">
              <div>
                <p className="text-dark-text-secondary">{t('dashboard.ops.kpi.reportsCanceled')}</p>
                <p className="text-base font-semibold text-dark-text">
                  {n(overview?.reports_canceled)}
                </p>
              </div>
              <div>
                <p className="text-dark-text-secondary">{t('dashboard.ops.kpi.unbansThisWeek')}</p>
                <p className="text-base font-semibold text-dark-text">
                  {n(overview?.unbans_this_week)}
                </p>
              </div>
            </div>
          )}
        </DashboardPanel>
        <DashboardPanel
          title={t('dashboard.ops.reportTypeBreakdown')}
          className="xl:col-span-4"
        >
          <ReportTypeBars reportsByType={overview?.reports_by_type} />
        </DashboardPanel>
        <DashboardPanel
          title={t('dashboard.ops.inactiveBreakdown')}
          className="xl:col-span-4"
        >
          <InactiveReasonBars inactiveByReason={overview?.inactive_by_reason} />
        </DashboardPanel>
      </div>
    </section>
  );
}
